import { App } from 'vue';
import { endpoints } from '@/api/endpoints';
import { trackClarity } from '@/tracking/clarity';
import { ClarityEvent } from '@/tracking/clarity/types';
import { t } from '@shared/helpers/translation';
import { configBase } from '@/config/base';
import { ModalType } from '@/store/general/types';
import { JavascriptError } from '@/api/Errors/errors.interfaces';
import { isNotNil } from '@shared/utils/value';
import { getMetrics } from '@/tracking/metrics';
import { StatsigEvent } from '@/tracking/statsig/types';
import { log } from '@libs/utils/log/log';
import { feature } from '@libs/utils/feature/feature';
import { classifyError } from '../api/Errors/error.helpers';
import store from '@/store';

export function setup(app: App<Element>): void {
    app.config.errorHandler = (error: any) => {
        handleError(error.message, error.stack, undefined, undefined);
    };

    window.onerror = (message, source, lineNumber, colNumber, error) => {
        if (error) {
            handleError(error.message, error.stack, lineNumber, colNumber);
        } else {
            handleError(message, source, lineNumber, colNumber);
        }
    };

    window.addEventListener('unhandledrejection', (event) => {
        const { reason } = event;
        const stackTrace = reason instanceof Error ? reason.stack || '' : '';
        handleError(`Unhandled rejection: ${reason}`, stackTrace, undefined, undefined).catch(
            (rejection) => {
                log('Error publishing', rejection);
            }
        );
    });
}

async function handleError(
    errorMessage: string | Event = 'unhandledrejection',
    stackTrace: string | undefined,
    lineNumber: number | undefined,
    colNumber: number | undefined
): Promise<void> {
    try {
        //displayErrorModal();
        trackClarity(ClarityEvent.SITENEO_UNHANDLED_ERROR);

        if (!stackTrace) {
            stackTrace = '';
        }
        if (lineNumber) {
            stackTrace += ` Line number: ${lineNumber}`;
        }
        if (colNumber) {
            stackTrace += ` Col number; ${colNumber}`;
        }

        const obj = {
            location: window.location.href,
            errorMessage: errorMessage.toString(),
            stackTrace,
        };

        if (configBase.isDevelopment) {
            if (!feature('hide-errors')) {
                console.error('error:', obj);
            }
            return;
        }

        log('Publishing error', obj);

        await LogError(obj, StatsigEvent.GENERAL_ERROR);
    } catch (ex) {
        log('An error occurred when handling an error', ex);
    }
}

export async function handleCustomError(errorMessage: string) {
    const obj = {
        location: window.location.href,
        errorMessage,
        stackTrace: '',
    };

    await LogError(obj, StatsigEvent.CUSTOM_ERROR);
}

export async function displayErrorModal(text: string, isKey: boolean = true): Promise<void> {
    const title = t({ key: 'shared.oops' });
    const message = isKey ? t({ key: text }) : text;
    // not logging here??

    await store.dispatch('general/setModal', {
        type: ModalType.Error,
        message,
        title,
    });
}

export async function LogError(error: JavascriptError, eventName: StatsigEvent) {
    const metrics = getMetrics();
    const classification = classifyError(error.errorMessage);
    const market = store.getters['general/getMarket'];
    const page = getPage();

    if (classification.shouldTrack) {
        metrics.error({
            type: eventName,
            error: new Error(error.errorMessage),
            context: {
                errorSource: classification.source,
                severity: classification.severity,
                errorCategory: classification.errorCategory,
                market: market?.number || 'unknown',
                locale: market?.locale || 'unknown',
                component: classification.component,
                page,
            },
        });
    }

    return await endpoints.error.PublishError(error, store.state);
}

export const getErrorMessage = (error: any): string | undefined => {
    if (!isNotNil(error)) {
        return undefined;
    }

    if (typeof error === 'string') {
        return error;
    } else if (error && typeof error.message === 'string') {
        return error.message;
    } else {
        return 'Unknown error';
    }
};

function getPage(): string {
    const page = store.getters['general/getPage'];
    if (page) return page;
    return getFirstSegment(window.location.pathname);
}

function getFirstSegment(path: string): string {
    const segment = path.split('/').filter(Boolean)[0];
    return segment || 'unknown';
}
